/**
 * Every rule of the game, in one object.
 *
 * ============================ WHY A CONFIG AND NOT CONSTANTS ====================
 * Blackjack is not one game, it is a family of them, and the differences move the
 * house edge by more than the whole edge of some of them. Dealer hitting soft 17
 * instead of standing is worth about 0.2% of a bet; blackjack paying 6:5 instead of
 * 3:2 is worth about 1.4%, which is larger than the total edge of the game we are
 * modelling. A codebase that hardcodes those decisions across a dozen files cannot
 * offer a second variant later without an audit of all of them.
 *
 * So the rules are DATA, read by the engine and by the analysis from the same object.
 * That second part is the one that matters: an analysis computed under different rules
 * from the game being played is worse than no analysis, because it is confidently
 * wrong and there is nothing on screen to say so.
 *
 * ## The house edge is not a target
 *
 * Nothing here is tuned to produce a particular edge. These are the rules of a common
 * double-deck game, and the edge is whatever the arithmetic makes it. If a future
 * variant is added, its edge is likewise a consequence, not a setting.
 * ================================================================================
 */

export interface BlackjackRules {
  /** Decks in the shoe. Two, and reshuffled every hand — see `freshShuffleEveryHand`. */
  decks: number;
  /**
   * A fresh shuffle before every hand, which is what makes counting impossible here.
   *
   * This is a deliberate product decision and not a simplification: a practice room
   * that could be counted would teach counting, and counting is not what this room is
   * for. It also makes the exact analysis honest — the composition the analysis
   * enumerates is the composition that is actually left, with no memory across hands.
   */
  freshShuffleEveryHand: boolean;
  /** Dealer stands on soft 17. `false` would mean the dealer hits it. */
  dealerStandsSoft17: boolean;
  /** Payout on a natural, as a multiple of the bet. 1.5 is 3:2. */
  blackjackPays: number;
  /** Double permitted on any two cards, rather than on 9/10/11 only. */
  doubleAnyTwo: boolean;
  /** Double after split. Worth roughly 0.14% of a bet when allowed. */
  doubleAfterSplit: boolean;
  /** Maximum hands one seat may end up with. 2 means a single split, no resplits. */
  maxHands: number;
  /** Aces receive exactly one card each and may not be resplit. */
  oneCardToSplitAces: boolean;
  /** Late surrender. Off — see the header of `settle`. */
  surrender: boolean;
  /**
   * Insurance is OFFERED, always, when the dealer shows an ace.
   *
   * It is a losing bet at every composition this game can reach, and that is exactly
   * why it is on the table: declining it correctly is a decision worth learning, and a
   * room that quietly refuses to offer it teaches nothing. The analysis states its EV
   * as a number every time, so the player is told what it costs rather than protected
   * from it.
   */
  offerInsurance: boolean;
  /** Insurance pays 2:1 on a half-bet side wager. */
  insurancePays: number;
}

export const DOUBLE_DECK_S17: BlackjackRules = {
  decks: 2,
  freshShuffleEveryHand: true,
  dealerStandsSoft17: true,
  blackjackPays: 1.5,
  doubleAnyTwo: true,
  doubleAfterSplit: true,
  maxHands: 2,
  oneCardToSplitAces: true,
  surrender: false,
  offerInsurance: true,
  insurancePays: 2,
};

/** Cards per shoe, derived rather than written twice. */
export const shoeSize = (r: BlackjackRules): number => r.decks * 52;

/**
 * A rank's blackjack value, and whether it is an ace.
 *
 * Ranks here are the poker engine's: 0 is a deuce, 8 is a ten, 9 through 11 are the
 * court cards, 12 is an ace. Sharing the card model with poker is deliberate — one
 * deck implementation, one shuffle, one verifier — and this is the only place the two
 * games disagree about what a card is worth.
 */
export const TEN_RANK = 8;
export const ACE_RANK = 12;

export function rankValue(rank: number): number {
  if (rank === ACE_RANK) return 11;
  return rank >= TEN_RANK ? 10 : rank + 2;
}

/** Index into a composition vector: 0-8 are 2..10 by value, 9 is the ace. */
export const COMP_SLOTS = 10;
export const ACE_SLOT = 9;

/**
 * Which slot a rank counts into.
 *
 * Ten, jack, queen and king are ONE slot, not four. They are indistinguishable to
 * every part of this game — same value, same dealer behaviour, same effect on every
 * EV — so collapsing them turns a 13-wide composition into a 10-wide one and makes the
 * enumeration roughly three times cheaper for free. Suits are dropped for the same
 * reason: blackjack never looks at them.
 */
export function rankSlot(rank: number): number {
  if (rank === ACE_RANK) return ACE_SLOT;
  return rank >= TEN_RANK ? 8 : rank;
}

/** How many of each slot a fresh shoe holds. Slot 8 (the tens) holds four ranks. */
export function freshComposition(r: BlackjackRules): Int32Array {
  const c = new Int32Array(COMP_SLOTS);
  for (let s = 0; s < COMP_SLOTS; s++) c[s] = 4 * r.decks;
  c[8] = 16 * r.decks;                      // T, J, Q, K
  return c;
}

/** Value of a slot, for the analysis, which works in slots rather than ranks. */
export const SLOT_VALUE: readonly number[] = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
